// ✅ src/pages/Asset/AssetTypeMasterListing.jsx
import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaChevronDown,
  FaChevronUp,
  FaSearch,
  FaPlus,
  FaEye,
  FaEdit,
  FaTrash,
} from "react-icons/fa";
import AssetLayoutWrapper from "../../components/AssetLayoutWrapper";
import { useNavigate } from "react-router-dom";

const API_BASE = "http://nissi-india.com/api/typeListing";

const AssetTypeMasterListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const navigate = useNavigate();

  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);

  const defaultFilters = {
    name: "",
    code: "",
    status: "Active",
  };

  const [filters, setFilters] = useState(defaultFilters);

  const [records, setRecords] = useState([]);

const [selectedIds, setSelectedIds] = useState([]);
const [selectedRows, setSelectedRows] = useState([]);

const [currentPage, setCurrentPage] = useState(1);
const pageSize = 10;

  // ✅ localStorage data
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branchType = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  // ✅ common headers for backend
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  // -------------------------
  // UI classes
  // -------------------------
  const inputClass = `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  // -------------------------
  // Helpers
  // -------------------------
  const normalizeHex = (c) => {
    if (!c) return "#000000";
    let s = String(c).trim();
    if (!s) return "#000000";
    if (!s.startsWith("#")) s = `#${s}`;
    return s;
  };

const mappedRecords = useMemo(() => {
  return (records || []).map((r) => ({
    id: r.id ?? r.fld_AssetTypeID ?? r.fld_AssetTypeId,
    assetTypeName: r.assetTypeName ?? r.fld_AssetTypeName,
    assetTypeCode: r.assetTypeCode ?? r.fld_AssetTypeCode,
    status: r.status ?? r.fld_status ?? r.fld_Status,
    colorCode: normalizeHex(r.colorCode ?? r.fld_colorCode ?? r.fld_colorCode),
    remarks: r.remarks ?? r.fld_Remarks ?? "",
  }));
}, [records]);

const totalPages = Math.max(1, Math.ceil(mappedRecords.length / pageSize));

const paginatedRecords = useMemo(() => {
  const start = (currentPage - 1) * pageSize;
  return mappedRecords.slice(start, start + pageSize);
}, [mappedRecords, currentPage]);

  // ✅ Active/Inactive counts for Results Summary panel
  const statusCounts = useMemo(() => {
    return mappedRecords.reduce(
      (acc, r) => {
        if (r.status === "Active") acc.active += 1;
        else if (r.status === "InActive") acc.inactive += 1;
        return acc;
      },
      { active: 0, inactive: 0 }
    );
  }, [mappedRecords]);

  const allSelected =
    mappedRecords.length > 0 && selectedIds.length === mappedRecords.length;

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds([]);
      setSelectedRows([]);
    } else {
      setSelectedIds(mappedRecords.map((r) => r.id));
      setSelectedRows(mappedRecords);
    }
  };

  const toggleRow = (row) => {
    const exists = selectedIds.includes(row.id);

    if (exists) {
      setSelectedIds((p) => p.filter((x) => x !== row.id));
      setSelectedRows((p) => p.filter((x) => x.id !== row.id));
    } else {
      setSelectedIds((p) => [...p, row.id]);
      setSelectedRows((p) => [...p, row]);
    }
  };

  const clearSelection = () => {
    setSelectedIds([]);
    setSelectedRows([]);
  };

  // -------------------------
  // API calls
  // -------------------------
const fetchAll = async () => {
  try {
    setLoading(true);
    clearSelection();

    const rs = await axios.get(`${API_BASE}/getAll`, {
      headers: commonHeaders,
    });

    setRecords(rs.data?.data || []);
    setCurrentPage(1);
    // toast.success("Loaded all records");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load records");
    } finally {
      setLoading(false);
    }
  };

const search = async () => {
  try {
    setLoading(true);
    clearSelection();

    const rs = await axios.get(`${API_BASE}/search`, {
      headers: commonHeaders,
      params: {
        name: filters.name,
        code: filters.code,
        status: filters.status,
      },
    });

    setRecords(rs.data?.data || []);
    setCurrentPage(1);
    // toast.info("Search completed");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Search failed");
    } finally {
      setLoading(false);
    }
  };

  const bulkUpdateStatus = async (status) => {
    if (selectedIds.length === 0) {
      toast.error("Select at least one row");
      return;
    }

    try {
      setLoading(true);

      await axios.put(
        `${API_BASE}/bulkStatus`,
        { ids: selectedIds, status },
        { headers: commonHeaders }
      );

      toast.success(`Updated status to ${status}`);
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Bulk update failed");
    } finally {
      setLoading(false);
    }
  };

  const bulkDelete = async () => {
    if (selectedIds.length === 0) {
      toast.error("Select at least one row");
      return;
    }

    if (!window.confirm(`Delete ${selectedIds.length} selected record(s)?`)) return;

    try {
      setLoading(true);

      await axios.delete(`${API_BASE}/bulkDelete`, {
        headers: commonHeaders,
        data: { ids: selectedIds },
      });

      toast.success("Deleted successfully");
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Bulk delete failed");
    } finally {
      setLoading(false);
    }
  };

  // -------------------------
  // Init
  // -------------------------
  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commonHeaders]);

  // -------------------------
  // Handlers
  // -------------------------
  const onFilterChange = (e) => {
    setFilters((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const clearFilters = () => {
    setFilters(defaultFilters);
    toast.success("Filters cleared");
  };

  const handleAddNew = () => {
    navigate("/Asset/AssetTypeMaster");
  };

  const handleEdit = (row) => {
    navigate(`/Asset/AssetTypeMaster?id=${row.id}`);
  };

  const handleDeleteSingle = async (row) => {
    if (!window.confirm(`Delete "${row.assetTypeName}" ?`)) return;

    try {
      setLoading(true);

      await axios.delete(`${API_BASE}/bulkDelete`, {
        headers: commonHeaders,
        data: { ids: [row.id] },
      });

      toast.success("Deleted");
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Delete failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AssetLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">ASSET TYPE MASTER</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* ==================== HEADER ==================== */}
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4 mb-6">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Asset Type Master Listing
              </h1>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {mappedRecords.length} Records
              </span>

              {!!selectedIds.length && (
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    isDark ? "bg-purple-900 text-purple-100" : "bg-purple-100 text-purple-800"
                  }`}
                >
                  {selectedIds.length} Selected
                </span>
              )}
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={handleAddNew}
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
              >
                <FaPlus />
                Add New
              </button>

              <button
                onClick={() => setShowFilters((p) => !p)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* ==================== SEARCH FILTERS ==================== */}
          {showFilters && (
            <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                  Search Criteria
                </h3>

                <div className="flex gap-2">
                  <button
                    onClick={clearFilters}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    Clear
                  </button>

                  <button
                    disabled={loading}
                    onClick={fetchAll}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${
                      loading
                        ? "opacity-60 cursor-not-allowed"
                        : isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    <FaEye /> View All
                  </button>

                  <button
                    disabled={loading}
                    onClick={search}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white ${
                      loading ? "opacity-60 cursor-not-allowed" : ""
                    }`}
                  >
                    <FaSearch /> Search Records
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <label className={labelClass}>Asset Type Name</label>
                  <input
                    type="text"
                    name="name"
                    value={filters.name}
                    onChange={onFilterChange}
                    className={inputClass}
                    placeholder="Enter asset type name"
                  />
                </div>

                <div>
                  <label className={labelClass}>Asset Type Code</label>
                  <input
                    type="text"
                    name="code"
                    value={filters.code}
                    onChange={onFilterChange}
                    className={inputClass}
                    placeholder="Enter asset type code"
                  />
                </div>

                <div>
                  <label className={labelClass}>Status</label>
                  <select name="status" value={filters.status} onChange={onFilterChange} className={inputClass}>
                    <option value="">All</option>
                    <option value="Active">Active</option>
                    <option value="InActive">InActive</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* ==================== BULK ACTIONS TOOLBAR ==================== */}
          <div className="flex flex-wrap gap-3 mb-6">
            <button
              disabled={loading || selectedIds.length === 0}
              onClick={() => bulkUpdateStatus("Active")}
              className={`px-4 py-2 rounded-lg font-semibold text-sm shadow transition ${
                selectedIds.length === 0
                  ? "bg-gray-400 text-white cursor-not-allowed opacity-60"
                  : "bg-green-600 hover:bg-green-700 text-white"
              }`}
            >
              Mark Active
            </button>

            <button
              disabled={loading || selectedIds.length === 0}
              onClick={() => bulkUpdateStatus("InActive")}
              className={`px-4 py-2 rounded-lg font-semibold text-sm shadow transition ${
                selectedIds.length === 0
                  ? "bg-gray-400 text-white cursor-not-allowed opacity-60"
                  : "bg-orange-600 hover:bg-orange-700 text-white"
              }`}
            >
              Mark InActive
            </button>

            <button
              disabled={loading}
              onClick={fetchAll}
              className={`px-4 py-2 rounded-lg font-semibold text-sm shadow transition ${
                isDark ? "bg-gray-700 hover:bg-gray-600 text-white" : "bg-gray-200 hover:bg-gray-300 text-gray-800"
              }`}
            >
              Reload
            </button>
          </div>

          {/* ==================== RESULTS TABLE ==================== */}
          <div className="overflow-x-auto rounded-lg border shadow-lg">
            {loading ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800 text-gray-300" : "bg-gray-50 text-gray-600"}`}>
                Loading...
              </div>
) : mappedRecords.length === 0 ? (
  <div className={`p-16 text-center ${isDark ? "bg-gray-800 text-gray-400" : "bg-gray-50 text-gray-500"}`}>
    <div className="text-lg font-medium mb-2">No Records Found!</div>
    <p className="text-sm">Use filters or click "View All".</p>
  </div>
) : (
  <>
    <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                  <thead className={`${tableHeaderClass} text-white`}>
                    <tr>
                      <th className="px-4 py-4 text-left text-sm font-semibold">S.No</th>
                      <th className="px-4 py-4 text-center text-sm font-semibold">Color</th>
                      <th className="px-4 py-4 text-left text-sm font-semibold">Code</th>
                      <th className="px-4 py-4 text-left text-sm font-semibold">Asset Type Name</th>
                      <th className="px-4 py-4 text-center text-sm font-semibold">Status</th>

                      {/* EDIT/DELETE - SECOND LAST COLUMN */}
                      <th className="px-4 py-4 text-center text-sm font-semibold">Actions</th>

                      {/* SELECT ALL - LAST COLUMN */}
                      <th className="px-4 py-4 text-center text-sm font-semibold w-16">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={toggleAll}
                          className="w-4 h-4 cursor-pointer"
                          title="Select All"
                        />
                      </th>
                    </tr>
                  </thead>

                  <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                    {paginatedRecords.map((row, idx) => {
                      const selected = selectedIds.includes(row.id);

                      return (
                        <tr
                          key={row.id}
                          className={`${
                            selected
                              ? isDark ? "bg-white/10" : "bg-blue-50"
                              : idx % 2 === 0
                              ? isDark ? "bg-gray-800" : "bg-white"
                              : isDark ? "bg-gray-750" : "bg-gray-50"
                          } transition-colors ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}
                        >
                          <td className={`px-4 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {idx + 1}
                          </td>

                          <td className="px-4 py-3 text-center">
                            <div
                              className="w-5 h-5 inline-block rounded-sm border"
                              style={{
                                backgroundColor: row.colorCode,
                                borderColor: isDark ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.2)",
                              }}
                              title={row.colorCode}
                            />
                          </td>

                         <td
  className={`px-4 py-3 text-sm font-medium max-w-[160px] truncate ${
    isDark ? "text-blue-400" : "text-blue-600"
  }`}
  title={row.assetTypeCode}
>
  {row.assetTypeCode}
</td>

                          <td className={`px-4 py-3 text-sm font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
                            {row.assetTypeName}
                          </td>

                          <td className="px-4 py-3 text-center">
                            <span
                              className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                row.status === "Active"
                                  ? isDark
                                    ? "bg-green-900 text-green-100"
                                    : "bg-green-100 text-green-800"
                                  : isDark
                                  ? "bg-red-900 text-red-100"
                                  : "bg-red-100 text-red-800"
                              }`}
                            >
                              {row.status}
                            </span>
                          </td>

                          {/* EDIT + DELETE */}
                          <td className="px-4 py-3 text-center">
                            <div className="flex justify-center gap-3">
                              <button
                                type="button"
                                onClick={() => handleEdit(row)}
                                className="text-yellow-500 hover:text-yellow-600 text-lg transition"
                                title="Edit"
                              >
                                ✏️
                              </button>

                              <button
                                type="button"
                                onClick={() => handleDeleteSingle(row)}
                                className="text-red-500 hover:text-red-600 text-lg transition"
                                title="Delete"
                              >
                                <FaTrash />
                              </button>
                            </div>
                          </td>

                          {/* SELECT */}
                          <td className="px-4 py-3 text-center">
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={() => toggleRow(row)}
                              className="w-4 h-4 cursor-pointer"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <div
                  className={`flex flex-wrap items-center justify-between gap-4 px-4 py-3 border-t ${
                    isDark ? "bg-gray-800 border-gray-700 text-gray-300" : "bg-white border-gray-200 text-gray-700"
                  }`}
                >
                  <span className="text-sm">
                    Page {currentPage} of {totalPages}
                  </span>

                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      className={`px-3 py-1.5 rounded-lg text-sm font-semibold border ${
                        currentPage === 1
                          ? "opacity-50 cursor-not-allowed border-gray-300"
                          : isDark
                          ? "border-gray-600 hover:bg-gray-700"
                          : "border-gray-300 hover:bg-gray-100"
                      }`}
                    >
                      Previous
                    </button>

                    <button
                      type="button"
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      className={`px-3 py-1.5 rounded-lg text-sm font-semibold border ${
                        currentPage === totalPages
                          ? "opacity-50 cursor-not-allowed border-gray-300"
                          : isDark
                          ? "border-gray-600 hover:bg-gray-700"
                          : "border-gray-300 hover:bg-gray-100"
                      }`}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </AssetLayoutWrapper>
  );
};

export default AssetTypeMasterListing;